// @ts-nocheck

import { some, reduce, every, filter, uniq, orderBy } from 'lodash';
import memoize from 'memoize-state';

import { getAnimationsPerNotableCase } from '../../constants/winModal';
import Formatter from '../../i18n/formatter';
import { __, getCurrency } from '../../i18n/l10n';
import { selectEntitiesBetslips } from '../../selectors/entities/betslips/entitiesBetslipsPrimary.selector';
import {
  selectEntitiesOutcomes,
  selectEntitiesOutcomesEntityLabel,
} from '../../selectors/entities/outcomes/entitiesOutcomesPrimary.selector';
import {
  selectOutcomeBetEntityBetTitle,
  selectOutcomeBetMatchCategoryEntitySportId,
  selectOutcomeBetMatchEntityTitle,
} from '../../selectors/outcome.selector';
import { BetslipTypes, BetslipStatus } from '../../types/history';
import { WinModalBetslip, WinModalAnimation } from '../../types/winModal';
import getBetslipNotableCase from '../../utils/getBetslipNotableCase';
import getSystemTitle from '../../utils/getSystemTitle';
import roundBoosterPercent from '../../utils/roundBoosterPercent';

const currency = getCurrency();

const getOutcomeInfos = (state, outcomeId) => {
  const betResult = selectEntitiesOutcomesEntityLabel(state, { outcomeId });
  const betTitle = selectOutcomeBetEntityBetTitle(state, { outcomeId });
  const sportId = selectOutcomeBetMatchCategoryEntitySportId(state, { outcomeId });
  const title = selectOutcomeBetMatchEntityTitle(state, { outcomeId });

  return {
    title,
    betResult,
    betTitle,
    sportId,
  };
};

const getEnhancedOutcomes = (state, outcomesDetails) => {
  const outcomes = selectEntitiesOutcomes(state);
  return outcomesDetails.map((outcome) => {
    const entityOutcome = outcomes[outcome.outcomeId];

    return {
      ...outcome,
      outcomeHasOccured: !!entityOutcome?.hasOccured,
      isBetbuilder: !!entityOutcome?.subOutcomes,
    };
  });
};

const getEnhancedBetlips = (state, betslips) =>
  reduce(
    betslips,
    (acc, cur) => {
      const { status, type, details } = cur ?? {};
      const enhancedOutcomes = getEnhancedOutcomes(state, details);
      const hasBetbuilder = some(enhancedOutcomes, 'isBetbuilder');
      const isCancelled = status === BetslipStatus.CANCELLED;
      const isSystem = type === BetslipTypes.SYSTEM;
      const allBetbuilder = every(enhancedOutcomes, 'isBetbuilder');
      const lostOutcomes = filter(enhancedOutcomes, { outcomeHasOccured: false });
      const oneLost = lostOutcomes && lostOutcomes.length === 1;
      const allLost = every(enhancedOutcomes, { outcomeHasOccured: false });
      return [...acc, { ...cur, hasBetbuilder, isSystem, isCancelled, allBetbuilder, oneLost, allLost }];
    },
    [],
  );

const getCustomPropsBetslips = (state, betslips) =>
  reduce(
    betslips,
    (acc, cur) => {
      const {
        status,
        betslipReference,
        isCancelled,
        type,
        details,
        betAmount,
        winningsAmount,
        winningsBonus,
        globalOdds,
        promotionCodeId,
        promotionTitle,
        hasBetbuilder,
        cnp,
        bankers,
        isSystem,
        allBetbuilder,
        oneLost,
        allLost,
      } = cur;

      let winnings = 0;
      if (winningsAmount !== null && winningsAmount !== undefined) {
        winnings = winningsAmount + (winningsBonus || 0);
      }

      let newBetslip = {
        winningsAmount: Formatter.formatNumber(winnings, currency),
        betAmount: `${__('Wager:')} ${Formatter.formatNumber(betAmount, currency)}`,
        odd: `${__('Odds:')} ${Formatter.formatOdd(globalOdds)}`,
        isCancelled,
        betslipReference,
      };

      // handle bonus
      const hasBonus = winningsBonus && winningsBonus > 0;
      if (hasBonus) {
        const bonusAmount = winningsBonus || 0;
        const isCombobooster = promotionCodeId && promotionCodeId.indexOf('BOOSTER-PERMANENT') !== -1;
        if (isCombobooster) {
          const boosterPercent = winnings ? roundBoosterPercent((bonusAmount / (winnings - betAmount)) * 100) : null;
          newBetslip = { ...newBetslip, boosterPercent };
        } else {
          const trimQuotes = promotionTitle.match(/^"(.+)"$/, '');
          const finalPromotionTitle = (trimQuotes && trimQuotes[1]) || promotionTitle;
          newBetslip = { ...newBetslip, promotionTitle: finalPromotionTitle };
        }
        newBetslip = { ...newBetslip, bonusAmount: Formatter.formatNumber(bonusAmount, currency) };
      }

      if (type === BetslipTypes.SINGLE) {
        const outcomeId = details[0].outcomeId;
        const outcomeInfos = getOutcomeInfos(state, outcomeId);
        newBetslip = { ...newBetslip, ...outcomeInfos };
        if (hasBetbuilder) {
          newBetslip = { ...newBetslip, isBetbuilder: true };
        }
      } else if (type === BetslipTypes.MULTIPLE) {
        const betsAccLabel = __('%1$s selection', details.length, '%1$s selections');
        newBetslip = {
          ...newBetslip,
          title: __('Accumulator %1$s', betsAccLabel),
        };
      } else if (isSystem) {
        const combinations = cnp && cnp.map((x) => getSystemTitle(details.length, bankers.length, x));
        const betsAccLabel = __('%1$s selection', details.length, '%1$s selections');
        newBetslip = {
          ...newBetslip,
          title: __('System %1$s', betsAccLabel),
          betTitle: combinations,
        };
      }

      const notableCase = getBetslipNotableCase(
        status,
        type,
        globalOdds,
        betAmount,
        winningsAmount,
        allBetbuilder,
        oneLost,
        allLost,
      );

      newBetslip = {
        ...newBetslip,
        animationType: getAnimationsPerNotableCase(notableCase),
      };

      return [...acc, newBetslip];
    },
    [],
  );

const getTotalAmount = (betslips) =>
  reduce(
    betslips,
    (acc, cur) => {
      const { winningsAmount, winningsBonus, status } = cur;
      if (status === BetslipStatus.SETTLED) {
        return acc + ((winningsAmount || 0) + (winningsBonus || 0));
      } else {
        return acc;
      }
    },
    0,
  );

const legacyWinModalContentPropsSelector = memoize((state, { betslipIds }) => {
  const betslips = selectEntitiesBetslips(state);
  let finalBetslips: Array<WinModalBetslip> = [];

  const storedBetslips = reduce(betslipIds, (acc, cur) => [...acc, betslips[cur]], []);
  const enhancedBetslips = getEnhancedBetlips(state, storedBetslips);
  finalBetslips = getCustomPropsBetslips(state, enhancedBetslips);
  finalBetslips = orderBy(finalBetslips, ['animationType'], ['desc']);
  const animationTypes: Array<WinModalAnimation> = uniq(finalBetslips.map((betslip) => betslip.animationType));
  const totalAmount: string = Formatter.formatNumber(getTotalAmount(storedBetslips), currency);

  return {
    animationTypes,
    totalAmount,
    betslips: finalBetslips,
  };
});

export default legacyWinModalContentPropsSelector;
